'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useReCaptcha } from 'next-recaptcha-v3'
import submitOrderStatusAction from '@/app/_plugins/HelpWidget/actions/orderStatusAction'
import FormInputError from './FormInputError'

const orderStatusSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Please enter a valid email'),
    order_id: z.string().min(1, 'Order ID is required'),
})

const OrderStatusForm = () => {
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [serverError, setServerError] = useState(null)
    const { executeRecaptcha } = useReCaptcha()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: zodResolver(orderStatusSchema),
        defaultValues: {
            email: '',
            order_id: '',
        }
    })

    const onSubmit = async (data) => {
        setServerError(null)

        try {
            const token = await executeRecaptcha('order_status')

            const response = await submitOrderStatusAction({
                ...data,
                recaptchaToken: token,
            })

            if (response.success) {
                setIsSubmitted(true)
            } else {
                setServerError(response.message || 'An error occurred. Please try again.')
            }
        } catch (err) {
            setServerError('An error occurred. Please try again.')
        }
    }

    const resetForm = () => {
        setIsSubmitted(false)
        setServerError(null)
        reset()
    }

    if (isSubmitted) {
        return (
            <div>
                <h3 className="text-lg font-medium mb-3">Thank You</h3>
                <p className="text-light mb-6">
                    The details of your order have been emailed to the address on file.
                </p>
                <button
                    onClick={resetForm}
                    className="btn btn-primary"
                >
                    check another order
                </button>
            </div>
        )
    }

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)}>
                {serverError && (
                    <div className="text-red mb-4">{serverError}</div>
                )}

                <div className="form-group mb-4">
                    <FormInputError message={errors.email?.message || ''} />
                    <input
                        type="email"
                        autoComplete="email"
                        className="form-input peer"
                        placeholder=" "
                        disabled={isSubmitting}
                        {...register('email')}
                    />
                    <label className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        email <span className="text-primary">*</span>
                    </label>
                </div>

                <div className="form-group mb-6">
                    <FormInputError message={errors.order_id?.message || ''} />
                    <input
                        type="text"
                        autoComplete="off"
                        className="form-input peer"
                        placeholder=" "
                        disabled={isSubmitting}
                        {...register('order_id')}
                    />
                    <label className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">
                        order id <span className="text-primary">*</span>
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn btn-primary w-full ${isSubmitting ? 'opacity-50' : ''}`}
                >
                    {isSubmitting ? 'submitting...' : 'submit request'}
                </button>
            </form>
        </div>
    )
}

export default OrderStatusForm
